import { useState } from 'react'
import { LoaderCircle } from 'lucide-react'
import { uploadWorklist } from '@/api/history'
import { getApiErrorMessage } from '@/lib/apiError'
import { formatSwedishDateTime } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import type { HistoryUploadResult } from '@/api/types'

const UPLOAD_ERROR_MESSAGE = 'Arbetslistan kunde inte läsas in.'
const NOT_JSON_MESSAGE = 'Filen är inte en giltig arbetslista (JSON).'

interface HistoryWorklistUploadProps {
  onUploaded: () => Promise<void>
}

export function HistoryWorklistUpload({ onUploaded }: HistoryWorklistUploadProps): React.ReactElement {
  const [file, setFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [result, setResult] = useState<HistoryUploadResult | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFile(event.target.files?.[0] ?? null)
    setResult(null)
    setErrorMessage(null)
  }

  const handleUpload = async (): Promise<void> => {
    if (file === null) return

    setIsUploading(true)
    setErrorMessage(null)

    try {
      const text = await file.text()
      let parsed: unknown

      try {
        parsed = JSON.parse(text)
      } catch {
        setErrorMessage(NOT_JSON_MESSAGE)

        return
      }

      const uploaded = await uploadWorklist(parsed)

      setResult(uploaded)
      await onUploaded()
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, UPLOAD_ERROR_MESSAGE))
    } finally {
      setIsUploading(false)
    }
  }

  const canUpload = file !== null && !isUploading

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader>
        <CardTitle>Läs in arbetslistan</CardTitle>
        <CardDescription>
          Filen worklist.json från avstämningen. Kunder som redan är inlagda behåller sin post; övriga ersätts. En fil för en senare omgång
          öppnar sina kunder igen, också de som redan är inlagda eller kontrollerade.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".json,application/json"
            onChange={handleFileChange}
            className="text-sm"
          />
          <Button
            className="rounded-none"
            disabled={!canUpload}
            onClick={handleUpload}
          >
            {isUploading && <LoaderCircle className="h-4 w-4 animate-spin" />}
            Läs in
          </Button>
        </div>
        {result && (
          <p className="text-sm text-muted-foreground">
            {result.customers} kunder i filen: {result.inserted} nya, {result.replaced} ersatta, {result.reopened} öppnade igen för en ny
            omgång, {result.keptApplied} behölls som de var. Plattformen läst {formatSwedishDateTime(result.frozen.prod)}, Capway{' '}
            {formatSwedishDateTime(result.frozen.capway)}.
          </p>
        )}
        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
      </CardContent>
    </Card>
  )
}
